import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAuth } from "./useAuth";
import { useAppLoading } from "./useAppLoading";
import { useAlert } from "./useAlert";
import { getErrorMessage } from "../utils/helpers";

interface IArticle {
  _id?: string;
  title?: string;
  en_title?: string;
  nl_title?: string;
  body?: string;
  richText?: string;
  en_richText?: string;
  nl_richText?: string;
  category?: string;
  image?: string;
  creator?: { first_name: string; last_name: string };
  createdAt?: string;
  updatedAt?: string;
  videoId?: string;
  pageViews?: string;
  cities?: string[];
  locationIds?: string[];
  locationType?: "CITY" | "LOCATION";
  homeSection?: "NEWS" | "DROPPIE";
  externalLink?: string;
  publishedAt?: string;
  /** Pinned = the app's TRENDING card. Several articles can be pinned at once,
   *  each aimed at a different place; readers see the one matching their default
   *  location. The two target lists are separate from cities/locationIds above,
   *  which only control where the article is visible at all. */
  isPinned?: boolean;
  pinnedCities?: string[];
  pinnedLocationIds?: string[];
}

interface ICategory {
  _id: string;
  title: string;
  createdAt?: string;
  updatedAt?: string;
}

interface IStore {
  /** State */
  article: IArticle | null;
  hasNextPage: boolean;
  articles: IArticle[];
  articlesError: string | null;
  articlesLoading: boolean;
  categories: ICategory[];
  articleCategory?: ICategory | null;
  addedArticle?: string | null;
  updatedArticle?: string | null;
  addedArticleCategory?: string | null;
  updatedArticleCategory?: string | null;
  /** Callbacks */
  getArticles: (page?: number, limit?: number, locationIds?: string[]) => void;
  getArticleCategory: (id: string) => void;
  getCategories: () => void;
  addArticle: (body: IArticle) => void;
  deleteArticle: (articleId: string) => void;
  addArticleCategory: (title: string) => void;
  updateArticleCategory: (articleId: string, title: string) => void;
  deleteArticleCategory: (articleId: string) => void;
  updateArticle: (articleId: string, body: IArticle) => void;
  pinArticle: (
    articleId: string,
    pinnedCities: string[],
    pinnedLocationIds: string[]
  ) => Promise<boolean>;
  unpinArticle: (articleId: string) => void;
  clearAddedArticle: () => void;
  clearUpdatedArticle: () => void;
  clearAddedArticleCategory: () => void;
  clearUpdatedArticleCategory: () => void;
  getArticle: (id: string) => void;
}

export const useArticles = create<IStore>()((set) => ({
  /** State */
  articles: [],
  hasNextPage: false,
  article: null,
  articlesError: null,
  articlesLoading: false,
  categories: [],
  articleCategory: null,
  addedArticle: null,
  addedArticleCategory: null,
  updatedArticleCategory: null,
  /** Callbacks */
  getArticleCategory: async (id) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`articles/categories/${id}`);
      set({ articleCategory: response.data });
    } catch (error) {
      LOG.error("Error on fetching Article Category", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getArticles: async (page = 1, limit = 20, locationIds) => {
    useAppLoading.getState().setIsLoading(true);
    set({
      articlesError: null,
      ...(page === 1 ? { articlesLoading: true } : {}),
    });
    try {
      let url = `articles?page=${page}&limit=${limit}&dashboard=true`;
      if (locationIds?.length) {
        const locationIdsParam = locationIds.join(',');
        url += `&locationIds=${locationIdsParam}`;
      }
      const response = await axios.get(url);
      set((state) => ({
        ...state,
        hasNextPage: response?.data?.length === limit,
        articles:
          page === 1 ? response?.data : [...state.articles, ...response?.data],
      }));
    } catch (error) {
      LOG.error("Error on fetching Articles", { error });
      set({ articlesError: getErrorMessage(error, "Failed to load articles.") });
    } finally {
      useAppLoading.getState().setIsLoading(false);
      set({ articlesLoading: false });
    }
  },
  getCategories: async () => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get("articles/categories");
      set({ categories: response.data });
    } catch (error) {
      LOG.error("Error on fetching Categories", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addArticle: async (body) => {
    useAppLoading.getState().setIsLoading(true);
    const creator = useAuth.getState().user?._id;
    try {
      const response = await axios.post("articles", {
        ...body,
        creator,
      });
      if (response?.data) {
        const newArticle = response.data;
        set((state) => ({
          articles: [newArticle, ...state.articles],
          addedArticle: newArticle._id,
        }));
      }
    } catch (error) {
      LOG.error("Add Article", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteArticle: async (articleId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      await axios.delete(`articles/${articleId}`);
      set((state) => ({
        articles: state.articles.filter((article) => article._id !== articleId),
      }));
    } catch (error) {
      LOG.error("Error on deleting an Article", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateArticle: async (articleId, body) => {
    useAppLoading.getState().setIsLoading(true);
    const creator = useAuth.getState().user?._id;
    try {
      const response = await axios.put(`articles/${articleId}`, {
        ...body,
        creator,
      });
      if (response?.data) {
        const updatedArticle = response.data;
        set((state) => ({
          articles: state.articles.map((article) => {
            if (article._id === updatedArticle._id) {
              return updatedArticle;
            }
            return article;
          }),
          updatedArticle: updatedArticle._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on updating an Article", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  // Resolves true when the pin stuck, so the modal knows whether to close. The
  // backend rejects a target that another pin already claims.
  pinArticle: async (articleId, pinnedCities, pinnedLocationIds) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post(`articles/${articleId}/pin`, {
        pinnedCities,
        pinnedLocationIds,
      });
      if (response?.data) {
        const pinned = response.data;
        // The dashboard listing includes pinned articles, so patch in place
        // rather than moving the row around.
        set((state) => ({
          articles: state.articles.map((article) =>
            article._id === pinned._id ? { ...article, ...pinned } : article
          ),
        }));
      }
      return true;
    } catch (error: any) {
      LOG.error("Error on pinning Article", { error });
      useAlert.getState().setAlert({
        isOpen: true,
        severity: "error",
        message: getErrorMessage(error, "Failed to pin the article."),
      });
      return false;
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  unpinArticle: async (articleId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post(`articles/${articleId}/unpin`);
      if (response?.data) {
        const unpinned = response.data;
        set((state) => ({
          articles: state.articles.map((article) =>
            article._id === unpinned._id
              ? { ...article, ...unpinned }
              : article
          ),
        }));
      }
    } catch (error) {
      LOG.error("Error on unpinning Article", { error });
      useAlert.getState().setAlert({
        isOpen: true,
        severity: "error",
        message: getErrorMessage(error, "Failed to unpin the article."),
      });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getArticle: async (id) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`articles/${id}`);
      set({ article: response.data });
    } catch (error) {
      LOG.error("Error on fetching Article", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addArticleCategory: async (title) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("articles/categories", { title });
      if (response?.data) {
        const newCategory = response.data;
        set((state) => ({
          categories: [...state.categories, newCategory],
          addedArticleCategory: newCategory._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on adding an Article Category", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateArticleCategory: async (articleId, title) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(`articles/categories/${articleId}`, {
        title,
      });
      if (response?.data) {
        const newCategory = response.data;
        set((state) => ({
          categories: state.categories.map((category) => {
            if (category._id === newCategory._id) {
              return newCategory;
            }
            return category;
          }),
          updatedArticleCategory: newCategory._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on adding an Article Category", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteArticleCategory: async (articleId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      await axios.delete(`articles/categories/${articleId}`);
      set((state) => ({
        categories: state.categories.filter(
          (category) => category._id !== articleId
        ),
      }));
    } catch (error) {
      LOG.error("Error on deleting an Article Category", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearUpdatedArticleCategory: () => {
    set({ updatedArticleCategory: null });
  },
  clearAddedArticleCategory: () => {
    set({ addedArticleCategory: null });
  },
  clearAddedArticle: () => {
    set({ addedArticle: null });
  },
  clearUpdatedArticle: () => {
    set({ updatedArticle: null });
  },
}));
